import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { Clock, Sparkles, Star, TrendingUp, Users, ExternalLink, ChevronRight, Twitter, Globe, MessageCircle, Copy } from 'lucide-react';
import { supabase, getSolanaTokenByAddress, getSolanaTokens, getSentryGroupPhotos } from '../utils/supabase';
import { getRelayTokenPriceSolana } from '../hooks/useRelayPrice';
import { formatMarketCap } from '../utils/address';
import { useRealtimePrices } from '../hooks/useRealtimePrices';
import { formatPriceDisplay } from '../utils/priceFormatter';
import { fetchTopGroups, checkApiHealth, fetchSwapFeedHistory, createSwapFeedConnection, type SwapEvent } from '../utils/sentryBotApi';
import TelegramLoginButton from '../components/sentry/TelegramLoginButton';
import SentryPriceFooter from '../components/sentry/SentryPriceFooter';
import UserGroupPortfolio from '../components/portfolio/UserGroupPortfolio';
import { fetchTelegramMe, loginTelegram, logoutTelegram, type TelegramAuthPayload, type TelegramUser } from '../utils/telegramAuth';
import { Connection, PublicKey, LAMPORTS_PER_SOL } from '@solana/web3.js';
import { getAssociatedTokenAddress } from '@solana/spl-token';
import TraderAnalytics from '../components/trader/TraderAnalytics';
import ComprehensiveAnalytics from '../components/analytics/ComprehensiveAnalytics';
import Top5Groups from '../components/groups/Top5Groups';
import SolanaTradeModal from '../components/shared/modals/SolanaTradeModal';
import { SolanaWalletProvider } from '../components/SolanaWalletProvider';

// ============================================
// SENTRY BOT DASHBOARD
// Analytics page for Sentry Trading Bot
// ============================================
const DEXSCREENER_ICON_URL = 'https://esjrycmiokijtxnbfyox.supabase.co/storage/v1/object/public/web-images/DEXS.png';
const ORCA_ICON_URL = 'https://esjrycmiokijtxnbfyox.supabase.co/storage/v1/object/public/web-images/ORCA-BUY.png';
const WEBSITE_ICON_URL = 'https://esjrycmiokijtxnbfyox.supabase.co/storage/v1/object/public/web-images/WEB.png';
const TWITTER_ICON_URL = 'https://esjrycmiokijtxnbfyox.supabase.co/storage/v1/object/public/web-images/X.png';
const TELEGRAM_ICON_URL = 'https://esjrycmiokijtxnbfyox.supabase.co/storage/v1/object/public/web-images/TG.png';
const GITBOOK_ICON_URL = 'https://esjrycmiokijtxnbfyox.supabase.co/storage/v1/object/public/web-images/Gitbook%20icon.png';
const LP_LOCK_ICON_URL = 'https://esjrycmiokijtxnbfyox.supabase.co/storage/v1/object/public/web-images/LPLock%20Icon.png';
const TVL_ICON_URL = 'https://esjrycmiokijtxnbfyox.supabase.co/storage/v1/object/public/web-images/TVL%20Icon.png';
const IFA_ICON_URL = 'https://esjrycmiokijtxnbfyox.supabase.co/storage/v1/object/public/web-images/IFA%20Icon.png';
const PWA_SWAP_ICON_URL = '/pwa-swap-icon.png';
const TELEGRAM_SWAP_ICON_URL = '/telegram-swap-icon.png';
const SENTRY_SWAP_ICON_URL = '/sentry-swap-icon.png';
const SOLANA_CHAIN_ID = 792703809;
const SENTRY_BOT_API_URL = (import.meta.env as any).VITE_SENTRY_BOT_API_URL ||
  (import.meta.env.DEV ? 'http://localhost:3001' : 'https://web-production-7d3e.up.railway.app');

const PageContainer = styled.div`
  padding: 24px;
  background: #000000;
  min-height: 100vh;
  width: 100%;
  padding-bottom: 84px;
  
  @media (max-width: 768px) {
    padding: 16px;
  }
`;

const TelegramSessionBadge = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 6px 10px;
  border-radius: 10px;
  border: 1px solid rgba(255, 255, 255, 0.08);
  background: rgba(255, 255, 255, 0.04);
  color: rgba(255, 255, 255, 0.8);
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.4px;
  text-transform: uppercase;
`;

const TelegramActionButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 6px 10px;
  border-radius: 10px;
  border: 1px solid rgba(255, 255, 255, 0.08);
  background: rgba(255, 255, 255, 0.06);
  color: rgba(255, 255, 255, 0.85);
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.4px;
  text-transform: uppercase;
  cursor: pointer;

  &:hover {
    color: #ffffff;
    background: rgba(255, 255, 255, 0.12);
  }
`;

// ============ HERO SECTION ============
const HeroSection = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(360px, 1fr));
  gap: 20px;
  margin-bottom: 32px;
  
  @media (max-width: 1400px) {
    grid-template-columns: repeat(auto-fit, minmax(320px, 1fr));
  }
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const HeroCard = styled.div<{ $featured?: boolean }>`
  background: rgba(20, 20, 25, 0.4);
  border: 1px solid rgba(255, 255, 255, 0.06);
  border-radius: 16px;
  padding: 20px;
  position: relative;
  overflow: hidden;
  transition: all 0.2s ease;
  cursor: pointer;
  height: 520px;
  display: flex;
  flex-direction: column;
  
  &:hover {
    border-color: rgba(255, 255, 255, 0.1);
  }
  
  @media (max-width: 768px) {
    height: auto;
    min-height: 240px;
  }
`;

const PortfolioHeroCard = styled(UserGroupPortfolio)`
  margin-bottom: 0;
  height: 520px;
  overflow: auto;
`;

// Sentry Token Display (Community Favorite replacement)
const SentryTokenContainer = styled.div`
  display: flex;
  gap: 24px;
  height: 100%;
  
  @media (max-width: 768px) {
    flex-direction: column;
    align-items: center;
  }
`;

const SentryTokenLogoWrapper = styled.div`
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const SentryTokenLogo = styled.img`
  width: 220px;
  height: 220px;
  border-radius: 50%;
  object-fit: cover;
  background: #000000;
  
  @media (max-width: 768px) {
    width: 150px;
    height: 150px;
  }
`;

const SentryTokenContent = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  min-width: 0;
  padding-top: 24px;
`;

const SentryTokenNameRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 8px;
  width: 100%;
`;

const SentryTokenName = styled.div`
  font-size: 24px;
  font-weight: 700;
  color: #ffffff;
`;

const SentryTokenSymbol = styled.div`
  font-size: 16px;
  color: rgba(255, 255, 255, 0.5);
  margin-bottom: 12px;
`;

const SentryTokenAddress = styled.div`
  font-size: 12px;
  color: rgba(255, 255, 255, 0.4);
  font-family: monospace;
  margin-bottom: 0;
`;

const SentryTokenAddressRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 6px;
`;

const CopyIconButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 22px;
  height: 22px;
  border-radius: 6px;
  border: 1px solid rgba(255, 255, 255, 0.08);
  background: rgba(255, 255, 255, 0.06);
  color: rgba(255, 255, 255, 0.7);
  cursor: pointer;

  &:hover {
    color: #ffffff;
    background: rgba(255, 255, 255, 0.12);
  }
`;

const SubtleLink = styled.a`
  font-size: 11px;
  color: rgba(255, 255, 255, 0.6);
  text-decoration: none;
  display: inline-flex;
  align-items: center;
  gap: 4px;

  &:hover {
    color: #ffffff;
    text-decoration: underline;
  }
`;

const SentryTokenLink = styled(SubtleLink)`
  margin-bottom: 16px;
`;

const IndexFundLink = styled(SubtleLink)`
  margin-bottom: 16px;
`;

const SentryTokenStat = styled.div`
  font-size: 20px;
  font-weight: 600;
  color: #10b981;
  margin-bottom: 8px;
`;

const SentryTokenDescription = styled.div`
  font-size: 13px;
  color: rgba(255, 255, 255, 0.5);
  line-height: 1.5;
  margin-bottom: 16px;
`;

const SentryTokenStats = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-bottom: 16px;
`;

const SentryTokenStatRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  font-size: 12px;
`;

const SentryTokenStatLabel = styled.span`
  color: rgba(255, 255, 255, 0.5);
`;

const SentryTokenStatValue = styled.span`
  color: #ffffff;
  font-weight: 600;
`;

const BackfillBadge = styled.span`
  display: inline-flex;
  align-items: center;
  padding: 2px 8px;
  border-radius: 999px;
  background: rgba(251, 191, 36, 0.15);
  color: #fbbf24;
  font-size: 10px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.4px;
`;

const SentryTokenPrice = styled.div`
  font-size: 18px;
  font-weight: 700;
  color: #10b981;
  margin-bottom: 8px;
`;

const SentryTokenSocialLinks = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  margin-top: auto;
  margin-bottom: 12px;
  flex-wrap: wrap;
`;

// Trading Group Spotlight Card (ZNS replacement)
const TradingGroupCard = styled(HeroCard)`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  padding: 24px;
`;

const TradingGroupIcon = styled.div`
  width: 80px;
  height: 80px;
  border-radius: 50%;
  background: linear-gradient(135deg, #a855f7, #7c3aed);
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 24px;
  
  svg {
    width: 40px;
    height: 40px;
    color: #ffffff;
  }
`;

const TradingGroupPhoto = styled.img`
  width: 80px;
  height: 80px;
  border-radius: 50%;
  object-fit: cover;
  background: linear-gradient(135deg, #a855f7, #7c3aed);
  margin-bottom: 24px;
  border: 2px solid rgba(255, 255, 255, 0.1);
`;

const TradingGroupTitle = styled.h3`
  font-size: 18px;
  font-weight: 700;
  color: #ffffff;
  margin: 0 0 6px 0;
`;

const TradingGroupSubtitle = styled.div`
  font-size: 12px;
  color: rgba(255, 255, 255, 0.5);
  margin-bottom: 16px;
`;

const TradingGroupStat = styled.div`
  font-size: 28px;
  font-weight: 700;
  color: #10b981;
  margin-bottom: 8px;
`;

const TradingGroupStatLabel = styled.div`
  font-size: 12px;
  color: rgba(255, 255, 255, 0.5);
  margin-bottom: 16px;
`;

const TradingGroupDescription = styled.div`
  font-size: 13px;
  color: rgba(255, 255, 255, 0.6);
  line-height: 1.5;
  margin-bottom: 24px;
  max-width: 280px;
`;

// Sentry Ecosystem Index Fund Card (Create Token replacement)
const IndexFundCard = styled(HeroCard)`
  justify-content: flex-start;
  align-items: stretch;
  text-align: left;
  padding: 20px;
`;

const IndexFundSplit = styled.div`
  display: flex;
  align-items: stretch;
  gap: 20px;
  width: 100%;

  @media (max-width: 768px) {
    flex-direction: column;
  }
`;

const IndexFundColumn = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
`;

const IndexFundDivider = styled.div`
  width: 1px;
  background: rgba(255, 255, 255, 0.08);

  @media (max-width: 768px) {
    width: 100%;
    height: 1px;
  }
`;

const IndexFundIcon = styled.div`
  width: 48px;
  height: 48px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.1);
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 12px;
  
  svg {
    width: 24px;
    height: 24px;
    color: rgba(255, 255, 255, 0.6);
  }

  img {
    width: 24px;
    height: 24px;
  }
`;

const IndexFundTitle = styled.div`
  font-size: 16px;
  font-weight: 600;
  color: #ffffff;
  margin-bottom: 8px;
`;

const IndexFundSubtitle = styled.div`
  font-size: 12px;
  color: rgba(255, 255, 255, 0.5);
  margin-bottom: 16px;
`;

const IndexFundStat = styled.div`
  font-size: 24px;
  font-weight: 600;
  color: #10b981;
  margin-bottom: 4px;
`;

const IndexFundSecondaryStat = styled.div`
  font-size: 16px;
  font-weight: 600;
  color: #10b981;
  margin-bottom: 4px;
`;

const IndexFundSubsection = styled.div`
  margin-top: 14px;
`;

const IndexFundStatLabel = styled.div`
  font-size: 11px;
  color: rgba(255, 255, 255, 0.4);
  margin-bottom: 16px;
`;

const TopAssetsContainer = styled.div`
  width: 100%;
  margin-top: 16px;
  padding-top: 16px;
  border-top: 1px solid rgba(255, 255, 255, 0.06);
`;

const TopAssetsTitle = styled.div`
  font-size: 11px;
  color: rgba(255, 255, 255, 0.5);
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin-bottom: 12px;
  text-align: left;
`;

const TopAssetItem = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 0;
  border-bottom: 1px solid rgba(255, 255, 255, 0.03);
  
  &:last-child {
    border-bottom: none;
  }
`;

const TopAssetLogo = styled.img`
  width: 24px;
  height: 24px;
  border-radius: 50%;
  object-fit: cover;
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.1);
  flex-shrink: 0;
`;

const TopAssetInfo = styled.div`
  flex: 1;
  text-align: left;
  min-width: 0;
`;

const TopAssetName = styled.div`
  font-size: 12px;
  font-weight: 600;
  color: #ffffff;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const TopAssetSymbol = styled.div`
  font-size: 10px;
  color: rgba(255, 255, 255, 0.5);
`;

const TopAssetValue = styled.div`
  font-size: 12px;
  font-weight: 600;
  color: #10b981;
  white-space: nowrap;
`;

// ============ TOKEN LIST SECTIONS ============
const ListSectionContainer = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 24px;
  margin-bottom: 32px;
  
  @media (max-width: 1100px) {
    grid-template-columns: repeat(2, 1fr);
  }
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 20px;
  }
`;

const ListSection = styled.div`
  background: rgba(20, 20, 25, 0.4);
  border: 1px solid rgba(255, 255, 255, 0.06);
  border-radius: 16px;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  height: 485px;
  
  @media (max-width: 768px) {
    height: auto;
    min-height: 300px;
  }
`;

const ListHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 20px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.06);
`;

const ListAnalytics = styled.div`
  padding: 12px 20px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.06);
  display: flex;
  gap: 24px;
  font-size: 12px;
`;

const AnalyticsStat = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const AnalyticsLabel = styled.div`
  color: rgba(255, 255, 255, 0.4);
  font-size: 11px;
`;

const AnalyticsValue = styled.div`
  color: #10b981;
  font-weight: 600;
  font-size: 14px;
`;

const ListTitle = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 14px;
  font-weight: 600;
  color: #ffffff;
  
  svg {
    width: 16px;
    height: 16px;
    color: rgba(255, 255, 255, 0.5);
  }
`;

const ListExpandButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 6px;
  color: rgba(255, 255, 255, 0.6);
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:hover {
    background: rgba(255, 255, 255, 0.1);
    color: #ffffff;
  }
  
  svg {
    width: 16px;
    height: 16px;
  }
`;

const ListContent = styled.div<{ $expanded?: boolean }>`
  flex: 1;
  overflow-y: auto;
  
  &::-webkit-scrollbar {
    width: 4px;
  }
  
  &::-webkit-scrollbar-track {
    background: transparent;
  }
  
  &::-webkit-scrollbar-thumb {
    background: rgba(255, 255, 255, 0.1);
    border-radius: 2px;
  }
`;

const TokenRow = styled.div`
  display: flex;
  align-items: center;
  padding: 10px 16px;
  gap: 10px;
  cursor: default;
  transition: all 0.2s ease;
  border-bottom: 1px solid rgba(255, 255, 255, 0.03);
  
  &:hover {
    background: #000000;
  }
  
  &:last-child {
    border-bottom: none;
  }
`;

const TokenLogo = styled.img`
  width: 36px;
  height: 36px;
  border-radius: 10px;
  object-fit: cover;
  background: #000000;
  border: 1px solid #333333;
  flex-shrink: 0;
`;

const TokenInfo = styled.div`
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

const TokenNameRow = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
`;

const TokenName = styled.div`
  font-size: 13px;
  font-weight: 600;
  color: #ffffff;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const TokenSymbol = styled.div`
  font-size: 11px;
  color: rgba(255, 255, 255, 0.5);
`;

const TokenStats = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  flex-shrink: 0;
`;

const TokenMetaStats = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  margin-top: 4px;
`;

const TokenMetaItem = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

const TokenMetaLabel = styled.span`
  font-size: 10px;
  text-transform: uppercase;
  letter-spacing: 0.4px;
  color: rgba(255, 255, 255, 0.45);
`;

const TokenMetaValue = styled.span`
  font-size: 11px;
  font-weight: 600;
  color: #ffffff;
`;

const TradeButton = styled.button<{ $disabled?: boolean }>`
  padding: 6px 12px;
  border-radius: 8px;
  border: 1px solid rgba(255, 255, 255, 0.15);
  background: ${({ $disabled }) => ($disabled ? 'rgba(255, 255, 255, 0.05)' : 'linear-gradient(135deg, #14F195 0%, #10b981 100%)')};
  color: ${({ $disabled }) => ($disabled ? 'rgba(255, 255, 255, 0.4)' : '#000000')};
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.4px;
  cursor: ${({ $disabled }) => ($disabled ? 'not-allowed' : 'pointer')};
  transition: all 0.2s ease;

  &:hover {
    ${({ $disabled }) => (!$disabled ? 'transform: translateY(-1px); box-shadow: 0 4px 12px rgba(20, 241, 149, 0.3);' : '')}
  }
`;

const TokenLink = styled.a`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 26px;
  height: 26px;
  transition: all 0.2s ease;
  
  &:hover {
    transform: translateY(-1px);
  }
`;

const TokenLinkIcon = styled.img`
  width: 26px;
  height: 26px;
  object-fit: contain;
`;

const TokenPrice = styled.div`
  font-size: 10px;
  color: rgba(255, 255, 255, 0.5);
`;

const PriceChange = styled.div<{ $positive?: boolean }>`
  font-size: 10px;
  font-weight: 500;
  color: ${(props: { $positive?: boolean }) => props.$positive ? '#10b981' : '#ef4444'};
`;

const EmptyState = styled.div`
  padding: 40px 20px;
  text-align: center;
  color: rgba(255, 255, 255, 0.4);
  font-size: 13px;
`;

// ============ SCROLLING TOKEN FEED ============
const ScrollingFeedContainer = styled.div`
  width: 100%;
  overflow: hidden;
  background: rgba(20, 20, 25, 0.4);
  border: 1px solid rgba(255, 255, 255, 0.06);
  border-radius: 16px;
  padding: 16px 0;
  margin-top: 24px;
`;

const ScrollingFeedTrack = styled.div`
  display: flex;
  animation: scroll 60s linear infinite;
  width: fit-content;
  
  &:hover {
    animation-play-state: paused;
  }
  
  @keyframes scroll {
    0% {
      transform: translateX(0);
    }
    100% {
      transform: translateX(-50%);
    }
  }
`;

const ScrollingTokenCard = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 20px;
  margin: 0 8px;
  background: rgba(255, 255, 255, 0.03);
  border: 1px solid rgba(255, 255, 255, 0.06);
  border-radius: 12px;
  cursor: pointer;
  transition: all 0.2s ease;
  white-space: nowrap;
  flex-shrink: 0;
  
  &:hover {
    background: #000000;
    border-color: #333333;
    transform: translateY(-2px);
  }
`;

const ScrollingTokenLogo = styled.img`
  width: 36px;
  height: 36px;
  border-radius: 50%;
  object-fit: cover;
  background: #000000;
`;

const ScrollingTokenInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

const ScrollingTokenSymbol = styled.div`
  font-size: 14px;
  font-weight: 600;
  color: #ffffff;
`;

const ScrollingTokenCategory = styled.div`
  font-size: 11px;
  color: rgba(255, 255, 255, 0.5);
  text-transform: capitalize;
`;

const ScrollingTokenMarketCap = styled.div`
  font-size: 13px;
  font-weight: 600;
  color: #10b981;
  margin-left: auto;
`;

// ============ LIVE SWAP FEED ============
const LiveFeedContainer = styled.div`
  margin-top: 32px;
  background: rgba(20, 20, 25, 0.4);
  border: 1px solid rgba(255, 255, 255, 0.06);
  border-radius: 16px;
  padding: 20px;
  max-height: 400px;
  overflow-y: auto;
  
  &::-webkit-scrollbar {
    width: 6px;
  }
  
  &::-webkit-scrollbar-track {
    background: rgba(255, 255, 255, 0.05);
    border-radius: 3px;
  }
  
  &::-webkit-scrollbar-thumb {
    background: rgba(255, 255, 255, 0.2);
    border-radius: 3px;
    
    &:hover {
      background: rgba(255, 255, 255, 0.3);
    }
  }
`;

const LiveFeedHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 16px;
  padding-bottom: 12px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.06);
`;

const LiveFeedTitle = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 16px;
  font-weight: 600;
  color: #ffffff;
`;

const LiveFeedIndicator = styled.div<{ $active: boolean }>`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: ${props => props.$active ? '#10b981' : '#ef4444'};
  animation: ${props => props.$active ? 'pulse 2s ease-in-out infinite' : 'none'};
  
  @keyframes pulse {
    0%, 100% {
      opacity: 1;
    }
    50% {
      opacity: 0.5;
    }
  }
`;

const SwapFeedList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const SwapFeedItem = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px;
  background: rgba(255, 255, 255, 0.02);
  border: 1px solid rgba(255, 255, 255, 0.04);
  border-radius: 12px;
  transition: all 0.2s ease;
  
  &:hover {
    background: rgba(255, 255, 255, 0.04);
    border-color: rgba(255, 255, 255, 0.08);
  }
`;

const SwapFeedText = styled.div`
  flex: 1;
  font-size: 13px;
  color: rgba(255, 255, 255, 0.8);
  line-height: 1.4;
`;

const SwapFeedMessage = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const SwapFeedIcon = styled.img`
  width: 16px;
  height: 16px;
  object-fit: contain;
`;

const SwapFeedUsername = styled.span`
  font-weight: 600;
  color: #ffffff;
`;

const SwapFeedAmount = styled.span`
  font-weight: 600;
  color: #10b981;
`;

const SwapFeedToken = styled.span`
  font-weight: 600;
  color: #3b82f6;
`;

const SwapFeedTime = styled.div`
  font-size: 11px;
  color: rgba(255, 255, 255, 0.4);
  white-space: nowrap;
`;

// ============ HELPER FUNCTIONS ============
const ensureHttpsUrl = (url: string | null | undefined): string => {
  if (!url) return '/token-placeholder.png';
  if (url.startsWith('Qm') && url.length === 46) {
    return `https://ipfs.io/ipfs/${url}`;
  }
  if (url.startsWith('http://')) {
    return url.replace('http://', 'https://');
  }
  return url;
};

const normalizeWebsiteUrl = (url?: string | null): string | null => {
  if (!url) return null;
  if (url.startsWith('http')) return url;
  return `https://${url}`;
};

const normalizeTwitterUrl = (url?: string | null): string | null => {
  if (!url) return null;
  if (url.startsWith('http')) return url;
  return `https://twitter.com/${url.replace('@', '')}`;
};

const normalizeTelegramUrl = (url?: string | null): string | null => {
  if (!url) return null;
  if (url.startsWith('http')) return url;
  return `https://t.me/${url.replace('@', '')}`;
};

const getTimeAgo = (date: Date): string => {
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffSecs = Math.floor(diffMs / 1000);
  const diffMins = Math.floor(diffSecs / 60);
  const diffHours = Math.floor(diffMins / 60);
  
  if (diffSecs < 60) return 'just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  return date.toLocaleDateString();
};

const toNumber = (value: unknown): number => {
  if (value === null || value === undefined) return 0;
  const num = Number(value);
  return Number.isFinite(num) ? num : 0;
};

const SOLSCAN_API_BASE = 'https://pro-api.solscan.io/v2.0';
const SOLSCAN_API_KEY = (import.meta.env as any).VITE_SOLSCAN_API_KEY as string | undefined;
const SOL_MINTS = new Set([
  'So11111111111111111111111111111111111111112', // WSOL
  'So11111111111111111111111111111111111111111'  // SOL (native placeholder)
]);

type SolscanPortfolioItem = {
  token_address?: string;
  mint_address?: string;
  address?: string;
  tokenAddress?: string;
  name?: string;
  token_name?: string;
  tokenName?: string;
  symbol?: string;
  token_symbol?: string;
  tokenSymbol?: string;
  logo_url?: string;
  token_icon?: string;
  icon?: string;
  amount?: number | string;
  token_amount?: number | string;
  balance?: number | string;
  ui_amount?: number | string;
  uiAmount?: number | string;
  price?: number | string;
  price_usd?: number | string;
  usd_value?: number | string;
  value?: number | string;
  underlying?: Array<Record<string, unknown>>;
};

const getSolscanHeaders = (): HeadersInit => {
  const headers: Record<string, string> = {
    accept: 'application/json'
  };
  if (SOLSCAN_API_KEY) {
    headers.token = SOLSCAN_API_KEY;
    headers.Authorization = `Bearer ${SOLSCAN_API_KEY}`;
    headers['x-api-key'] = SOLSCAN_API_KEY;
  }
  return headers;
};

const extractUnderlyingUsdValue = (underlying?: Array<Record<string, unknown>>): number | null => {
  if (!Array.isArray(underlying) || underlying.length === 0) return null;
  const total = underlying.reduce((sum, item) => {
    const value = toNumber(item?.usd_value ?? item?.value);
    if (value > 0) return sum + value;
    const amount = toNumber(item?.amount ?? item?.token_amount ?? item?.ui_amount ?? item?.balance);
    const price = toNumber(item?.price ?? item?.price_usd);
    return sum + (amount > 0 && price > 0 ? amount * price : 0);
  }, 0);
  return total > 0 ? total : null;
};

const fetchSolscanPortfolio = async (walletAddress: string): Promise<Array<{
  mint: string;
  balance: number;
  usdValue: number;
  name: string;
  symbol: string;
  logoUrl: string | null;
  isLp: boolean;
  lpUsdValue: number | null;
}>> => {
  const response = await fetch(`${SOLSCAN_API_BASE}/account/portfolio?address=${walletAddress}`, {
    method: 'GET',
    headers: getSolscanHeaders()
  });

  if (!response.ok) {
    throw new Error(`Solscan portfolio request failed: ${response.status}`);
  }

  const payload = await response.json();
  const items: SolscanPortfolioItem[] = Array.isArray(payload?.data)
    ? payload.data
    : Array.isArray(payload)
      ? payload
      : [];

  return items
    .map((item) => {
      const mint = item.token_address || item.mint_address || item.address || item.tokenAddress || '';
      const balance =
        toNumber(item.ui_amount ?? item.uiAmount ?? item.amount ?? item.token_amount ?? item.balance);
      const underlyingUsdValue = extractUnderlyingUsdValue(item.underlying);
      const directUsdValue = toNumber(item.usd_value ?? item.value);
      const price = toNumber(item.price ?? item.price_usd);
      const usdValue =
        underlyingUsdValue ??
        (directUsdValue > 0 ? directUsdValue : balance > 0 && price > 0 ? balance * price : 0);
      const name = item.token_name || item.tokenName || item.name || 'Unknown Token';
      const symbol = item.token_symbol || item.tokenSymbol || item.symbol || 'UNK';
      const logoUrl = item.token_icon || item.logo_url || item.icon || null;

      return {
        mint,
        balance,
        usdValue,
        name,
        symbol,
        logoUrl,
        isLp: underlyingUsdValue !== null,
        lpUsdValue: underlyingUsdValue
      };
    })
    .filter((item) => item.mint && item.balance > 0)
    .filter((item) => !SOL_MINTS.has(item.mint) && item.symbol !== 'SOL');
};

// Determine text color based on swap message content
const getSwapTextColor = (message: string): string => {
  const lowerMessage = message.toLowerCase();
  
  // Check for $SENTRY bought messages (neon gold/yellow) - check this first
  if (lowerMessage.includes('$sentry') && lowerMessage.includes('bought')) {
    return '#FFD700'; // Neon gold/yellow
  }
  
  // Check for call or dca (green)
  if (lowerMessage.includes("dca'd") || lowerMessage.includes("dca") || lowerMessage.includes('called')) {
    return '#10b981'; // Green
  }
  
  // Check for sell or take profit (red)
  if (lowerMessage.includes('sold') || lowerMessage.includes('took profit')) {
    return '#ef4444'; // Red
  }
  
  // Default color
  return 'rgba(255, 255, 255, 0.8)';
};

const formatUsdAmount = (amount: number | null | undefined): string => {
  if (amount === null || amount === undefined) return '--';
  return `$${amount.toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  })}`;
};

const getSwapIconSource = (swap: SwapEvent): string | null => {
  const source = swap.data?.source;
  if (source === 'pwa') return PWA_SWAP_ICON_URL;
  if (source === 'telegram') return TELEGRAM_SWAP_ICON_URL;
  if (source === 'sentry_buy') return SENTRY_SWAP_ICON_URL;

  const lowerMessage = swap.message.toLowerCase();
  if (lowerMessage.includes('$sentry') && lowerMessage.includes('bought')) {
    return SENTRY_SWAP_ICON_URL;
  }
  if (
    lowerMessage.includes("dca'd") ||
    lowerMessage.includes('swapped') ||
    lowerMessage.includes('took profit') ||
    lowerMessage.includes('called')
  ) {
    return TELEGRAM_SWAP_ICON_URL;
  }
  if (swap.type === 'buy' || swap.type === 'sell') {
    return PWA_SWAP_ICON_URL;
  }
  return null;
};


const getFeeSwapWalletCount = (summary: SentryFeeSwapSummary | null): number => {
  if (!summary) return 0;
  if (Array.isArray(summary.statsByWallet) && summary.statsByWallet.length > 0) {
    return summary.statsByWallet.length;
  }
  const walletKeys = summary.wallets ? Object.keys(summary.wallets).filter(Boolean) : [];
  return walletKeys.length;
};

// Solana RPC connection - use environment variable with fallback
const getSolanaConnection = (): Connection => {
  const rpcUrl = (import.meta.env as any).VITE_SOLANA_RPC_URL || 'https://solana.publicnode.com';
  return new Connection(rpcUrl, 'confirmed');
};

// Sentry token address
const SENTRY_TOKEN_ADDRESS = '55592jXxdwmCxERy2YmpJMi7MGcqJ6kwYJ2Ztrro7XfX';
// Sentry fees wallet
const SENTRY_FEES_WALLET = 'C9ocu16Z4ZhQ2irbUukmdr1y6W7eyXRYmBFu4SeBh2Ef';
const SENTRY_FEES_WALLET_SOLSCAN_URL = `https://solscan.io/account/${SENTRY_FEES_WALLET}#portfolio`;
const SENTRY_TOKEN_SOLSCAN_URL = `https://solscan.io/token/${SENTRY_TOKEN_ADDRESS}`;
const SENTRY_GITBOOK_URL = 'https://mavrk.gitbook.io/sentry-docs/sentry-token/sentry-token-sentry';
const SENTRY_LP_LOCK_URL = 'https://solscan.io/tx/Jfi7MnjZgxDeDuNivurrQNiGDvqKbQJ73DbanxhLjnsCdVrg3TSMg2sigYZ2Fzd6UXej4TB7XX6C4rW5vQZCYng';

// ============ COMPONENT ============
const SentryDashboardContent: React.FC = () => {
  // Data states
  const [sentryToken, setSentryToken] = useState<any>(null);
  const [sentryTokenPrice, setSentryTokenPrice] = useState<number | null>(null);
  const [sentryTokenVolume24h, setSentryTokenVolume24h] = useState<number | null>(null);
  const [recentlyDeployed, setRecentlyDeployed] = useState<any[]>([]);
  const [scrollingTokens, setScrollingTokens] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [sentryTvlUsd, setSentryTvlUsd] = useState<number | null>(null);
  const [sentryTmsUsd, setSentryTmsUsd] = useState<number | null>(null);
  const [sentryTvlLoading, setSentryTvlLoading] = useState(true);
  const [isTradeModalOpen, setIsTradeModalOpen] = useState(false);
  const [selectedToken, setSelectedToken] = useState<{
    address: string;
    symbol: string;
    name: string;
    logo?: string | null;
    decimals?: number;
    poolAddress?: string;
    tokenVaultA?: string;
    tokenVaultB?: string;
    tickSpacing?: number;
  } | null>(null);
  const { getPriceForToken, loading: pricesLoading } = useRealtimePrices(SOLANA_CHAIN_ID);

  const handleCopySentryAddress = async () => {
    try {
      await navigator.clipboard.writeText(SENTRY_TOKEN_ADDRESS);
    } catch (error) {
      console.error('Failed to copy SENTRY address:', error);
    }
  };
  
  // Index Fund data
  const [indexFundValue, setIndexFundValue] = useState<{
    sol: number;
    usd: number;
    tokenCount: number;
    lpUsd: number;
  } | null>(null);
  
  // Top 3 assets in Index Fund
  const [topAssets, setTopAssets] = useState<Array<{
    mint: string;
    balance: number;
    usdValue: number;
    name: string;
    symbol: string;
    logoUrl: string | null;
  }>>([]);
  
  // Expansion states
  const [recentlyDeployedExpanded, setRecentlyDeployedExpanded] = useState(false);
  
  // Live swap feed state
  const [recentSwaps, setRecentSwaps] = useState<SwapEvent[]>([]);

  const [telegramUser, setTelegramUser] = useState<TelegramUser | null>(null);
  const [telegramAuthError, setTelegramAuthError] = useState<string | null>(null);
  const [telegramLoading, setTelegramLoading] = useState(false);
  const telegramBotUsername = (import.meta.env as any).VITE_TELEGRAM_BOT_USERNAME as string | undefined;

  const handleTelegramAuth = async (payload: TelegramAuthPayload) => {
    setTelegramLoading(true);
    setTelegramAuthError(null);
    try {
      const user = await loginTelegram(payload);
      setTelegramUser(user);
    } catch (error: any) {
      setTelegramAuthError(error?.message || 'Telegram login failed.');
    } finally {
      setTelegramLoading(false);
    }
  };

  const handleTelegramLogout = async () => {
    setTelegramLoading(true);
    setTelegramAuthError(null);
    try {
      await logoutTelegram();
      setTelegramUser(null);
    } catch (error: any) {
      setTelegramAuthError(error?.message || 'Failed to disconnect Telegram.');
    } finally {
      setTelegramLoading(false);
    }
  };

  const telegramHeaderAction = telegramUser ? (
    <>
      <TelegramSessionBadge>
        Connected{telegramUser.username ? ` @${telegramUser.username}` : ''}
      </TelegramSessionBadge>
      <TelegramActionButton onClick={handleTelegramLogout} disabled={telegramLoading}>
        Disconnect
      </TelegramActionButton>
    </>
  ) : (
    <TelegramLoginButton botUsername={telegramBotUsername} onAuth={handleTelegramAuth} />
  );
  
  // Fetch Sentry token data
  useEffect(() => {
    const fetchSentryToken = async () => {
      try {
        const token = await getSolanaTokenByAddress(SENTRY_TOKEN_ADDRESS);
        if (token) {
          setSentryToken(token);
          // Fetch price using Relay API for Solana (chain ID 792703809)
          const price = await getRelayTokenPriceSolana(SENTRY_TOKEN_ADDRESS);
          setSentryTokenPrice(price);
          
          // Fetch DexScreener data for 24h volume
          try {
            const dexScreenerResponse = await fetch(
              `https://api.dexscreener.com/latest/dex/tokens/${SENTRY_TOKEN_ADDRESS}`
            );
            if (dexScreenerResponse.ok) {
              const dexData = await dexScreenerResponse.json();
              // Get the first pair's 24h volume (usually the most liquid)
              if (dexData.pairs && dexData.pairs.length > 0) {
                const pair = dexData.pairs[0];
                const volume24h = pair.volume?.h24 || pair.volume24h || 0;
                setSentryTokenVolume24h(volume24h);
              }
            }
          } catch (error) {
            console.warn('Error fetching DexScreener data:', error);
          }
        }
      } catch (error) {
        console.error('Error fetching Sentry token:', error);
      }
    };
    
    fetchSentryToken();
  }, []);

  useEffect(() => {
    let cancelled = false;
    const loadTelegramUser = async () => {
      try {
        const user = await fetchTelegramMe();
        if (!cancelled) {
          setTelegramUser(user);
        }
      } catch (error) {
        if (!cancelled) {
          setTelegramUser(null);
        }
      }
    };

    loadTelegramUser();

    return () => {
      cancelled = true;
    };
  }, []);
  
  // Fetch recently deployed tokens
  useEffect(() => {
    const fetchRecentlyDeployed = async () => {
      try {
        const tokens = await getSolanaTokens({
          orderBy: 'created_at',
          ascending: false,
          limit: 20
        });
        setRecentlyDeployed(tokens);
        
        // Set scrolling tokens (duplicate for seamless loop)
        if (tokens.length > 0) {
          if (tokens.length >= 3) {
            setScrollingTokens([...tokens, ...tokens]);
          } else {
            setScrollingTokens([...tokens, ...tokens, ...tokens, ...tokens]);
          }
        }
      } catch (error) {
        console.error('Error fetching recently deployed:', error);
      }
    };
    
    fetchRecentlyDeployed();
  }, []);

  useEffect(() => {
    const fetchSentryTvl = async () => {
      try {
        const response = await fetch(`${SENTRY_BOT_API_URL}/api/tvl`);
        if (!response.ok) {
          throw new Error(`Failed to fetch TVL: ${response.status}`);
        }
        const data = await response.json();
        const totalValueUsd = typeof data?.totalValueUsd === 'number' ? data.totalValueUsd : null;
        const totalMarketSizeUsd = typeof data?.totalMarketSizeUsd === 'number' ? data.totalMarketSizeUsd : null;
        setSentryTvlUsd(totalValueUsd);
        setSentryTmsUsd(totalMarketSizeUsd);
      } catch (error) {
        console.warn('[SentryDashboard] Failed to fetch TVL:', error);
      } finally {
        setSentryTvlLoading(false);
      }
    };

    fetchSentryTvl();
  }, []);
  
  // Fetch Index Fund value (Sentry fees wallet holdings)
  useEffect(() => {
    const fetchIndexFund = async () => {
      try {
        const connection = getSolanaConnection();
        const walletPubkey = new PublicKey(SENTRY_FEES_WALLET);
        
        // Get SOL balance
        const solBalance = await connection.getBalance(walletPubkey);
        const solBalanceFormatted = solBalance / LAMPORTS_PER_SOL;
        
        // Get SOL price from Relay API (using WSOL address)
        const WSOL_ADDRESS = 'So11111111111111111111111111111111111111112';
        const solPrice = await getRelayTokenPriceSolana(WSOL_ADDRESS) || 150; // Fallback to $150 if API fails
        const solValueUSD = solBalanceFormatted * solPrice;
        
        let totalTokenValueUSD = 0;
        let tokenCount = 0;
        let lpUsdTotal = 0;
        let topAssetsWithMetadata: Array<{
          mint: string;
          balance: number;
          usdValue: number;
          name: string;
          symbol: string;
          logoUrl: string | null;
        }> = [];

        // Prefer Solscan portfolio (includes LP valuations + underlying breakdown)
        try {
          const solscanTokens = await fetchSolscanPortfolio(walletPubkey.toBase58());
          if (solscanTokens.length > 0) {
            totalTokenValueUSD = solscanTokens.reduce((sum, token) => sum + token.usdValue, 0);
            lpUsdTotal = solscanTokens
              .filter((token) => token.isLp && token.lpUsdValue !== null)
              .reduce((sum, token) => sum + (token.lpUsdValue || 0), 0);
            tokenCount = solscanTokens.length;
            topAssetsWithMetadata = solscanTokens
              .sort((a, b) => b.usdValue - a.usdValue)
              .slice(0, 3);
          }
        } catch (error) {
          console.warn('Solscan portfolio fetch failed, falling back to on-chain pricing:', error);
        }

        if (topAssetsWithMetadata.length === 0) {
          // Get SPL token accounts (both Token Program and Token-2022)
          const TOKEN_PROGRAM_ID = new PublicKey('TokenkegQfeZyiNwAJbNbGKPFXCWuBvf9Ss623VQ5DA');
          const TOKEN_2022_PROGRAM_ID = new PublicKey('TokenzQdBNbLqP5VEhdkAS6EPFLC1PHnBqCXEpPxuEb');

          const tokenAccounts = await connection.getParsedTokenAccountsByOwner(walletPubkey, {
            programId: TOKEN_PROGRAM_ID
          });

          const tokenAccounts2022 = await connection.getParsedTokenAccountsByOwner(walletPubkey, {
            programId: TOKEN_2022_PROGRAM_ID
          });

          const allTokenAccounts = [...tokenAccounts.value, ...tokenAccounts2022.value];
          const tokenData: Array<{ mint: string; balance: number }> = [];

          for (const account of allTokenAccounts) {
            const parsedInfo = account.account.data.parsed?.info;
            if (!parsedInfo) continue;

            const mint = parsedInfo.mint;
            const balance = parsedInfo.tokenAmount?.uiAmount || 0;

            if (balance > 0) {
              tokenCount++;
              tokenData.push({ mint, balance });
            }
          }

          // Fetch prices and calculate USD values for all tokens
          const BATCH_SIZE = 5;
          const tokenValues: Array<{ mint: string; balance: number; usdValue: number }> = [];

          for (let i = 0; i < tokenData.length; i += BATCH_SIZE) {
            const batch = tokenData.slice(i, i + BATCH_SIZE);
            const batchPromises = batch.map(async ({ mint, balance }) => {
              try {
                const price = await getRelayTokenPriceSolana(mint);
                if (price && price > 0) {
                  const usdValue = balance * price;
                  tokenValues.push({ mint, balance, usdValue });
                  return usdValue;
                }
              } catch (e) {
                console.warn(`Failed to fetch price for token ${mint}:`, e);
              }
              return 0;
            });

            const batchValues = await Promise.all(batchPromises);
            totalTokenValueUSD += batchValues.reduce((sum, val) => sum + val, 0);

            // Small delay between batches to avoid rate limiting
            if (i + BATCH_SIZE < tokenData.length) {
              await new Promise(resolve => setTimeout(resolve, 200));
            }
          }

          // Sort by USD value and get top 3
          const top3Tokens = tokenValues
            .sort((a, b) => b.usdValue - a.usdValue)
            .slice(0, 3);

          // Fetch metadata for top 3 tokens from Supabase
          topAssetsWithMetadata = await Promise.all(
            top3Tokens.map(async ({ mint, balance, usdValue }) => {
              try {
                const tokenMetadata = await getSolanaTokenByAddress(mint);
                return {
                  mint,
                  balance,
                  usdValue,
                  name: tokenMetadata?.name || 'Unknown Token',
                  symbol: tokenMetadata?.symbol || 'UNK',
                  logoUrl: tokenMetadata?.logo_url || null
                };
              } catch (error) {
                console.warn(`Failed to fetch metadata for token ${mint}:`, error);
                return {
                  mint,
                  balance,
                  usdValue,
                  name: 'Unknown Token',
                  symbol: 'UNK',
                  logoUrl: null
                };
              }
            })
          );
        }

        setTopAssets(topAssetsWithMetadata);
        setIndexFundValue({
          sol: solBalanceFormatted,
          usd: solValueUSD + totalTokenValueUSD,
          tokenCount,
          lpUsd: lpUsdTotal
        });
      } catch (error) {
        console.error('Error fetching index fund:', error);
      } finally {
        setLoading(false);
      }
    };
    
    fetchIndexFund();
  }, []);

  // Fetch live swap feed using SSE
  useEffect(() => {
    console.log('[SentryDashboard] Setting up swap feed SSE connection...');
    
    // Fetch initial history
    const loadHistory = async () => {
      try {
        const history = await fetchSwapFeedHistory(50);
        if (history.length > 0) {
          // Reverse to show newest first
          setRecentSwaps([...history].reverse());
          console.log('[SentryDashboard] Loaded', history.length, 'swap events from history');
        }
      } catch (error) {
        console.error('[SentryDashboard] Error loading swap history:', error);
      }
    };
    
    loadHistory();
    
    // Set up SSE connection for real-time updates
    const { eventSource, cleanup } = createSwapFeedConnection(
      // onEvent - new swap event received
      (event) => {
        console.log('[SentryDashboard] New swap event:', event);
        setRecentSwaps(prev => {
          // Add new event to the beginning, remove duplicates, limit to 100
          const newSwaps = [event, ...prev];
          const unique = new Map();
          newSwaps.forEach(swap => {
            if (!unique.has(swap.id)) {
              unique.set(swap.id, swap);
            }
          });
          return Array.from(unique.values()).slice(0, 100);
        });
      },
      // onHistory - initial history received via SSE
      (events) => {
        console.log('[SentryDashboard] Received history via SSE:', events.length, 'events');
        if (events.length > 0) {
          // Reverse to show newest first
          setRecentSwaps([...events].reverse().slice(0, 100));
        }
      },
      // onError
      (error) => {
        console.error('[SentryDashboard] Swap feed SSE error:', error);
      }
    );
    
    return () => {
      console.log('[SentryDashboard] Cleaning up swap feed connection');
      cleanup();
    };
  }, []);
  
  const displayedRecentlyDeployed = recentlyDeployedExpanded ? recentlyDeployed : recentlyDeployed.slice(0, 5);
  
  const handleTokenClick = (token: any) => {
    // Open DexScreener page
    const address = token.contract_address || token.mint_address;
    if (address) {
      window.open(`https://dexscreener.com/solana/${address}`, '_blank');
    }
  };

  const handleTradeClick = (token: any) => {
    const mintAddress = token.contract_address || token.mint_address;
    if (!mintAddress || !token.pool_address) {
      return;
    }

    setSelectedToken({
      address: mintAddress,
      symbol: token.symbol,
      name: token.name,
      logo: token.logo_url,
      decimals: token.decimals || 9,
      poolAddress: token.pool_address,
      tokenVaultA: token.token_vault_a,
      tokenVaultB: token.token_vault_b,
      tickSpacing: token.tick_spacing || 128
    });
    setIsTradeModalOpen(true);
  };
  
  return (
    <PageContainer>
      {/* ============ HERO SECTION ============ */}
      <HeroSection>
        {/* Sentry Token - Large Card (Community Favorite replacement) */}
        <HeroCard $featured>
          {sentryToken ? (
            <SentryTokenContainer>
              <SentryTokenLogoWrapper>
                <SentryTokenLogo 
                  src={ensureHttpsUrl(sentryToken.logo_url)} 
                  alt={sentryToken.name}
                  onError={(e: React.SyntheticEvent<HTMLImageElement>) => { (e.target as HTMLImageElement).src = '/token-placeholder.png'; }}
                />
              </SentryTokenLogoWrapper>
              
              <SentryTokenContent>
                <SentryTokenNameRow>
                  <SentryTokenName>{sentryToken.name}</SentryTokenName>
                  <TradeButton
                    onClick={() => handleTradeClick(sentryToken)}
                    $disabled={!sentryToken.pool_address}
                    disabled={!sentryToken.pool_address}
                  >
                    {sentryToken.pool_address ? 'Trade' : 'No Pool'}
                  </TradeButton>
                </SentryTokenNameRow>
                <SentryTokenSymbol>${sentryToken.symbol}</SentryTokenSymbol>
                <SentryTokenAddressRow>
                  <SentryTokenAddress>
                    {SENTRY_TOKEN_ADDRESS.slice(0, 8)}...{SENTRY_TOKEN_ADDRESS.slice(-6)}
                  </SentryTokenAddress>
                  <CopyIconButton
                    type="button"
                    onClick={handleCopySentryAddress}
                    title="Copy contract address"
                    aria-label="Copy SENTRY contract address"
                  >
                    <Copy size={12} />
                  </CopyIconButton>
                </SentryTokenAddressRow>
                <SentryTokenLink href={SENTRY_TOKEN_SOLSCAN_URL} target="_blank" rel="noopener noreferrer">
                  View on Solscan
                </SentryTokenLink>
                
                {/* Price per token */}
                {sentryTokenPrice !== null && (
                  <SentryTokenPrice>
                    {formatPriceDisplay(sentryTokenPrice)}
                  </SentryTokenPrice>
                )}
                
                {/* Trading Stats */}
                <SentryTokenStats>
                  {sentryTokenVolume24h !== null && sentryTokenVolume24h > 0 && (
                    <SentryTokenStatRow>
                      <SentryTokenStatLabel>24h Volume</SentryTokenStatLabel>
                      <SentryTokenStatValue>
                        ${sentryTokenVolume24h >= 1000 
                          ? (sentryTokenVolume24h / 1000).toFixed(2) + 'K'
                          : sentryTokenVolume24h.toLocaleString('en-US', { 
                              minimumFractionDigits: 2, 
                              maximumFractionDigits: 2 
                            })
                        }
                      </SentryTokenStatValue>
                    </SentryTokenStatRow>
                  )}
                </SentryTokenStats>
                
                <SentryTokenDescription>
                  {sentryToken.description || 'The native token of the Sentry Trading Bot ecosystem'}
                </SentryTokenDescription>
                
                {/* Social Links */}
                <SentryTokenSocialLinks>
                  {normalizeWebsiteUrl(sentryToken.website) && (
                    <TokenLink href={normalizeWebsiteUrl(sentryToken.website) || undefined} target="_blank" rel="noopener noreferrer">
                      <TokenLinkIcon src={WEBSITE_ICON_URL} alt="Website" />
                    </TokenLink>
                  )}
                  <TokenLink
                    href={`https://dexscreener.com/solana/${SENTRY_TOKEN_ADDRESS}`}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <TokenLinkIcon src={DEXSCREENER_ICON_URL} alt="Dexscreener" />
                  </TokenLink>
                  <TokenLink
                    href={`https://www.orca.so/?tokenIn=So11111111111111111111111111111111111111112&tokenOut=${encodeURIComponent(SENTRY_TOKEN_ADDRESS)}`}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <TokenLinkIcon src={ORCA_ICON_URL} alt="Orca buy" />
                  </TokenLink>
                  {normalizeTwitterUrl(sentryToken.twitter) && (
                    <TokenLink href={normalizeTwitterUrl(sentryToken.twitter) || undefined} target="_blank" rel="noopener noreferrer">
                      <TokenLinkIcon src={TWITTER_ICON_URL} alt="Twitter" />
                    </TokenLink>
                  )}
                  {normalizeTelegramUrl(sentryToken.telegram) && (
                    <TokenLink href={normalizeTelegramUrl(sentryToken.telegram) || undefined} target="_blank" rel="noopener noreferrer">
                      <TokenLinkIcon src={TELEGRAM_ICON_URL} alt="Telegram" />
                    </TokenLink>
                  )}
                  <TokenLink href={SENTRY_GITBOOK_URL} target="_blank" rel="noopener noreferrer">
                    <TokenLinkIcon src={GITBOOK_ICON_URL} alt="Gitbook" />
                  </TokenLink>
                  <TokenLink href={SENTRY_LP_LOCK_URL} target="_blank" rel="noopener noreferrer">
                    <TokenLinkIcon src={LP_LOCK_ICON_URL} alt="Liquidity lock" />
                  </TokenLink>
                </SentryTokenSocialLinks>
              </SentryTokenContent>
            </SentryTokenContainer>
          ) : (
            <EmptyState>Loading Sentry token...</EmptyState>
          )}
        </HeroCard>
        
        {/* Sentry Ecosystem Index Fund (Create Token replacement) */}
        <IndexFundCard>
          <IndexFundSplit>
            <IndexFundColumn>
              <IndexFundIcon>
                <img src={TVL_ICON_URL} alt="TVL" />
              </IndexFundIcon>
              <IndexFundTitle>Sentry Ecosystem TVL</IndexFundTitle>
              <IndexFundSubtitle>Total Value Locked</IndexFundSubtitle>
              <IndexFundStat>
                {sentryTvlLoading ? '--' : formatUsdAmount(sentryTvlUsd)}
              </IndexFundStat>
              <IndexFundSubsection>
                <IndexFundTitle>Sentry Ecosystem TMS</IndexFundTitle>
                <IndexFundSubtitle>Total Market Size</IndexFundSubtitle>
                <IndexFundSecondaryStat>
                  {sentryTvlLoading ? '--' : formatUsdAmount(sentryTmsUsd)}
                </IndexFundSecondaryStat>
              </IndexFundSubsection>
            </IndexFundColumn>
            <IndexFundDivider />
            <IndexFundColumn>
              <IndexFundIcon>
                <img src={IFA_ICON_URL} alt="Index Fund Assets" />
              </IndexFundIcon>
              <IndexFundTitle>Index Fund Assets</IndexFundTitle>
              <IndexFundSubtitle>Fees & Holdings Wallet</IndexFundSubtitle>
              <IndexFundLink href={SENTRY_FEES_WALLET_SOLSCAN_URL} target="_blank" rel="noopener noreferrer">
                View on Solscan
              </IndexFundLink>
              {indexFundValue ? (
                <>
                  <IndexFundStat>
                    ${indexFundValue.usd.toLocaleString()}
                  </IndexFundStat>
                  <IndexFundStatLabel>
                    {indexFundValue.sol.toFixed(2)} SOL • {indexFundValue.tokenCount} tokens
                  </IndexFundStatLabel>
                  {indexFundValue.lpUsd > 0 && (
                    <IndexFundStatLabel>
                      LP Underlying Value: ${indexFundValue.lpUsd.toLocaleString('en-US', {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2
                      })}
                    </IndexFundStatLabel>
                  )}
                  
                  {topAssets.length > 0 && (
                    <TopAssetsContainer>
                      <TopAssetsTitle>Top Holdings</TopAssetsTitle>
                      {topAssets.map((asset) => (
                        <TopAssetItem key={asset.mint}>
                          {asset.logoUrl ? (
                            <TopAssetLogo 
                              src={ensureHttpsUrl(asset.logoUrl) || ''} 
                              alt={asset.symbol}
                              onError={(e: React.SyntheticEvent<HTMLImageElement>) => {
                                (e.target as HTMLImageElement).style.display = 'none';
                              }}
                            />
                          ) : (
                            <TopAssetLogo 
                              src="/token-placeholder.png" 
                              alt={asset.symbol}
                            />
                          )}
                          <TopAssetInfo>
                            <TopAssetName>{asset.name}</TopAssetName>
                            <TopAssetSymbol>${asset.symbol}</TopAssetSymbol>
                          </TopAssetInfo>
                          <TopAssetValue>
                            ${asset.usdValue.toLocaleString('en-US', { 
                              minimumFractionDigits: 2, 
                              maximumFractionDigits: 2 
                            })}
                          </TopAssetValue>
                        </TopAssetItem>
                      ))}
                    </TopAssetsContainer>
                  )}
                </>
              ) : (
                <EmptyState>Loading fund data...</EmptyState>
              )}
            </IndexFundColumn>
          </IndexFundSplit>
        </IndexFundCard>

        {/* Comprehensive Analytics */}
        <ComprehensiveAnalytics />

        {/* Portfolio View */}
        <PortfolioHeroCard
          telegramUser={telegramUser}
          headerAction={telegramHeaderAction}
          statusMessage={telegramAuthError}
        />
      </HeroSection>
      
      {/* ============ TOKEN LIST SECTIONS ============ */}
      <ListSectionContainer>
        {/* Recently Deployed */}
        <ListSection>
          <ListHeader>
            <ListTitle>
              <Clock /> Recently Deployed
            </ListTitle>
            <ListExpandButton onClick={() => setRecentlyDeployedExpanded(!recentlyDeployedExpanded)}>
              <ChevronRight style={{ transform: recentlyDeployedExpanded ? 'rotate(90deg)' : 'none' }} />
            </ListExpandButton>
          </ListHeader>
          <ListContent $expanded={recentlyDeployedExpanded}>
            {displayedRecentlyDeployed.length > 0 ? (
              displayedRecentlyDeployed.map((token, index) => {
                const tokenAddress = token.contract_address || token.mint_address;
                const priceUSD = tokenAddress ? getPriceForToken(tokenAddress, SOLANA_CHAIN_ID) : null;
                const marketCap = priceUSD ? formatMarketCap(priceUSD) : '--';
                const websiteUrl = normalizeWebsiteUrl(token.website);
                const twitterUrl = normalizeTwitterUrl(token.twitter);
                const telegramUrl = normalizeTelegramUrl(token.telegram);
                const dexscreenerUrl = tokenAddress ? `https://dexscreener.com/solana/${tokenAddress}` : null;

                return (
                  <TokenRow key={token.id || index}>
                    <TokenLogo 
                      src={ensureHttpsUrl(token.logo_url)} 
                      alt={token.name}
                      onError={(e: React.SyntheticEvent<HTMLImageElement>) => { (e.target as HTMLImageElement).src = '/token-placeholder.png'; }}
                    />
                    <TokenInfo>
                      <TokenNameRow>
                        <TokenName>{token.name}</TokenName>
                      </TokenNameRow>
                      <TokenSymbol>${token.symbol}</TokenSymbol>
                      <TokenMetaStats>
                        <TokenMetaItem>
                          <TokenMetaLabel>Market Cap</TokenMetaLabel>
                          <TokenMetaValue>{pricesLoading ? '--' : marketCap}</TokenMetaValue>
                        </TokenMetaItem>
                        <TokenMetaItem>
                          <TokenMetaLabel>Price</TokenMetaLabel>
                          <TokenMetaValue>{pricesLoading ? '--' : formatPriceDisplay(priceUSD)}</TokenMetaValue>
                        </TokenMetaItem>
                      </TokenMetaStats>
                    </TokenInfo>
                    <TokenStats>
                      <TradeButton
                        onClick={() => handleTradeClick(token)}
                        $disabled={!token.pool_address}
                        disabled={!token.pool_address}
                      >
                        {token.pool_address ? 'Trade' : 'No Pool'}
                      </TradeButton>
                      {websiteUrl && (
                        <TokenLink href={websiteUrl} target="_blank" rel="noopener noreferrer">
                          <TokenLinkIcon src={WEBSITE_ICON_URL} alt="Website" />
                        </TokenLink>
                      )}
                      {dexscreenerUrl && (
                        <TokenLink href={dexscreenerUrl} target="_blank" rel="noopener noreferrer">
                          <TokenLinkIcon src={DEXSCREENER_ICON_URL} alt="Dexscreener" />
                        </TokenLink>
                      )}
                      {twitterUrl && (
                        <TokenLink href={twitterUrl} target="_blank" rel="noopener noreferrer">
                          <TokenLinkIcon src={TWITTER_ICON_URL} alt="Twitter" />
                        </TokenLink>
                      )}
                      {telegramUrl && (
                        <TokenLink href={telegramUrl} target="_blank" rel="noopener noreferrer">
                          <TokenLinkIcon src={TELEGRAM_ICON_URL} alt="Telegram" />
                        </TokenLink>
                      )}
                    </TokenStats>
                  </TokenRow>
                );
              })
            ) : (
              <EmptyState>No tokens found</EmptyState>
            )}
          </ListContent>
        </ListSection>
        
        {/* Top 5 Groups */}
        <Top5Groups />
        
        {/* Trader Analytics */}
        <TraderAnalytics />
      </ListSectionContainer>
      
      {/* ============ SCROLLING TOKEN FEED ============ */}
      {scrollingTokens.length > 0 && (
        <ScrollingFeedContainer>
          <ScrollingFeedTrack>
            {[...scrollingTokens, ...scrollingTokens]
              .map((token, index) => {
                return (
                  <ScrollingTokenCard 
                    key={`${token.id}-${index}`}
                    onClick={() => handleTokenClick(token)}
                  >
                    <ScrollingTokenLogo 
                      src={ensureHttpsUrl(token.logo_url)} 
                      alt={token.symbol}
                      onError={(e: React.SyntheticEvent<HTMLImageElement>) => { 
                        (e.target as HTMLImageElement).style.display = 'none';
                      }}
                    />
                    <ScrollingTokenInfo>
                      <ScrollingTokenSymbol>${token.symbol}</ScrollingTokenSymbol>
                      <ScrollingTokenCategory>{token.category || 'Token'}</ScrollingTokenCategory>
                    </ScrollingTokenInfo>
                    <ScrollingTokenMarketCap>
                      <ExternalLink size={12} />
                    </ScrollingTokenMarketCap>
                  </ScrollingTokenCard>
                );
              })}
          </ScrollingFeedTrack>
        </ScrollingFeedContainer>
      )}
      
      {/* ============ LIVE SWAP FEED ============ */}
      <LiveFeedContainer>
        <LiveFeedHeader>
          <LiveFeedTitle>
            <LiveFeedIndicator $active={recentSwaps.length > 0} />
            Live Swap Feed
          </LiveFeedTitle>
        </LiveFeedHeader>
        <SwapFeedList>
          {recentSwaps.length > 0 ? (
            recentSwaps.map((swap) => {
              const timeAgo = getTimeAgo(new Date(swap.timestamp));
              const textColor = getSwapTextColor(swap.message);
              const lowerMessage = swap.message.toLowerCase();
              const isGreen = lowerMessage.includes("dca'd") || lowerMessage.includes("dca") || lowerMessage.includes('called');
              const isRed = lowerMessage.includes('sold') || lowerMessage.includes('took profit');
              const isGold = lowerMessage.includes('$sentry') && lowerMessage.includes('bought');
              const iconSource = getSwapIconSource(swap);
              
              return (
                <SwapFeedItem 
                  key={swap.id} 
                  style={{ 
                    borderLeftColor: isGreen ? '#10b981' : isRed ? '#ef4444' : isGold ? '#FFD700' : 'transparent', 
                    borderLeftWidth: (isGreen || isRed || isGold) ? '3px' : '0px' 
                  }}
                >
                  <SwapFeedText style={{ color: textColor }}>
                    {iconSource ? (
                      <SwapFeedMessage>
                        <SwapFeedIcon src={iconSource} alt="Swap source" />
                        <span>{swap.message}</span>
                      </SwapFeedMessage>
                    ) : (
                      swap.message
                    )}
                  </SwapFeedText>
                  <SwapFeedTime>{timeAgo}</SwapFeedTime>
                </SwapFeedItem>
              );
            })
          ) : (
            <EmptyState>
              No swaps yet. Swaps will appear here as they happen!
              <br />
              <small style={{ color: 'rgba(255, 255, 255, 0.3)', fontSize: '11px', marginTop: '8px', display: 'block' }}>
                Waiting for swap events from /api/swap-feed...
              </small>
            </EmptyState>
          )}
        </SwapFeedList>
      </LiveFeedContainer>
      <SolanaTradeModal
        isOpen={isTradeModalOpen}
        onClose={() => {
          setIsTradeModalOpen(false);
          setSelectedToken(null);
        }}
        token={selectedToken}
      />
      <SentryPriceFooter />
    </PageContainer>
  );
};

const SentryDashboard: React.FC = () => (
  <SolanaWalletProvider>
    <SentryDashboardContent />
  </SolanaWalletProvider>
);

export default SentryDashboard;
